import React from "react";
import { LuCalendar, LuCreditCard } from "react-icons/lu";

import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import {
  getCustomerPortalUrl,
  getRenewalLabel,
  getScheduledChangeLabel,
} from "./subscriptionPresentation";
import "./subscriptionStatus.css";

const getStatusBadgeVariant = (status) => {
  if (status === "past_due" || status === "canceled") return "critical";
  if (status === "paused") return "warning";
  return "success";
};

const SubscriptionStatusCard = ({
  subscription,
  onChangePlan,
  onChoosePlan,
}) => {
  if (!subscription) {
    return (
      <div aria-busy="true" aria-label="Loading subscription" className="subscription-status-card subscription-status-card--loading">
        <Skeleton className="subscription-skeleton subscription-skeleton--small" />
        <Skeleton className="subscription-skeleton subscription-skeleton--large" />
        <Skeleton className="subscription-skeleton subscription-skeleton--medium" />
      </div>
    );
  }

  const scheduledChangeLabel = getScheduledChangeLabel(subscription);
  const renewalLabel = getRenewalLabel(subscription);
  const manageUrl = getCustomerPortalUrl(subscription.manageUrl);
  const showManage = subscription.canManage && Boolean(manageUrl);
  const isCanceled = subscription.status === "canceled";

  return (
    <article className="subscription-status-card" aria-labelledby="subscription-status-title">
      <header className="subscription-status-card__header">
        <div>
          <h2 id="subscription-status-title" className="subscription-status-card__title">
            {subscription.title}
          </h2>
        </div>
        <Badge className="subscription-status-card__status" variant={getStatusBadgeVariant(subscription.status)}>
          {subscription.statusLabel}
        </Badge>
      </header>

      <div className="subscription-status-card__summary">
        <div>
          <p className="subscription-status-card__label">Plan</p>
          <p className="subscription-status-card__plan-name">{subscription.planName}</p>
          {subscription.billingIntervalLabel && (
            <p className="subscription-status-card__muted">
              Billed every {subscription.billingIntervalLabel}
            </p>
          )}
        </div>
        <div className="subscription-status-card__price-block">
          <p className="subscription-status-card__label">Total</p>
          <p className="subscription-status-card__price">{subscription.totalLabel}</p>
          {subscription.discount && subscription.subtotalLabel !== subscription.totalLabel && (
            <p className="subscription-status-card__muted">
              from {subscription.subtotalLabel}
            </p>
          )}
          {subscription.billingIntervalLabel && (
            <p className="subscription-status-card__muted">/ {subscription.billingIntervalLabel}</p>
          )}
        </div>
      </div>

      {(subscription.discount || scheduledChangeLabel) && (
        <div className="subscription-status-card__notices">
          {subscription.discount && (
            <div className="subscription-status-card__notice subscription-status-card__notice--discount">
              <span>{subscription.discount.code}</span>
              {subscription.discount.amount && <strong>{subscription.discount.amount} off</strong>}
              {subscription.discount.expiresAt && <span>until {subscription.discount.expiresAt}</span>}
            </div>
          )}
          {scheduledChangeLabel && (
            <div className="subscription-status-card__notice subscription-status-card__notice--scheduled">
              {scheduledChangeLabel}
            </div>
          )}
        </div>
      )}

      <div className="subscription-status-card__details">
        {subscription.nextBillingLabel && !isCanceled && (
          <div className="subscription-status-card__detail">
            <span className="subscription-status-card__detail-label">
              <LuCalendar className="subscription-status-card__detail-icon" size={15} aria-hidden="true" />
              Next billing
            </span>
            <strong>{subscription.nextBillingLabel}</strong>
          </div>
        )}
        {renewalLabel && (
          <div className="subscription-status-card__detail">
            <span className="subscription-status-card__detail-label">
              <LuCreditCard className="subscription-status-card__detail-icon" size={15} aria-hidden="true" />
              Billing status
            </span>
            <strong>{renewalLabel}</strong>
          </div>
        )}
      </div>

      {(onChangePlan || showManage || (isCanceled && onChoosePlan)) && (
        <footer className="subscription-status-card__footer">
          {onChangePlan && !isCanceled && (
            <Button className="subscription-status-card__action" onClick={onChangePlan} type="button" variant="primary">
              Change plan
            </Button>
          )}
          {showManage && (
            <a
              className="subscription-status-card__manage-link"
              href={manageUrl}
              rel="noopener noreferrer"
              target="_blank"
            >
              Manage subscription
            </a>
          )}
          {isCanceled && onChoosePlan && (
            <Button className="subscription-status-card__action" onClick={onChoosePlan} type="button" variant="primary">
              Choose a plan
            </Button>
          )}
        </footer>
      )}
    </article>
  );
};

export default SubscriptionStatusCard;
